import { Project } from '@/types/cmsTypes';
import type { CreatorProject } from '@/infrastructure/clients/creatorApi';
import projectPlaceholder from '@/assets/project-historical.jpg';

export type ProjectStatus = 'draft' | 'live' | 'archived' | 'pending';

const computeProjectStatus = (
  statuses: Array<string | null | undefined>
): ProjectStatus => {
  if (!statuses.length) return 'draft';
  if (statuses.some((s) => s === 'live')) return 'live';
  if (statuses.some((s) => s === 'pending')) return 'pending';
  if (statuses.some((s) => s === 'draft' || s === 'template')) return 'draft';
  if (statuses.some((s) => s === 'archived')) return 'archived';
  return 'draft';
};

const pickHeroImage = (images: CreatorProject['images']): string => {
  const byCategory = (category: string) =>
    images
      .filter((img) => img.category === category)
      .sort((a, b) => (a.orderNumber ?? Number.MAX_SAFE_INTEGER) - (b.orderNumber ?? Number.MAX_SAFE_INTEGER))[0];
  const chosen = byCategory('ProjectLandscape') ?? byCategory('ProjectPortrait');
  return chosen?.croppedUrl || chosen?.originalUrl || projectPlaceholder;
};

const pickSceneScreenshot = (scenes: CreatorProject['scenes']): string | null =>
  [...scenes]
    .filter((s) => !!s.coverImgUrl)
    .sort((a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime())[0]?.coverImgUrl ?? null;

// v1 carries location_info as JSON
const parseLocationInfo = (raw: unknown): Project['locationInfo'] => {
  if (!raw) return { latitude: 0, longitude: 0 };
  try {
    const parsed = (typeof raw === 'string' ? JSON.parse(raw) : raw) as { coordinates?: { lat?: number; lng?: number }; address?: string } | null;
    const coords = parsed?.coordinates;
    if (!coords) return { latitude: 0, longitude: 0, address: parsed?.address };
    return {
      latitude: coords.lat ?? 0,
      longitude: coords.lng ?? 0,
      address: parsed?.address
    };
  } catch {
    return { latitude: 0, longitude: 0 };
  }
};

export const convertToProjectFormat = (dto: CreatorProject): Project => {
  const { project } = dto;
  const updated = new Date(project.updatedAt ?? project.createdAt ?? 0);
  const coverImage = pickHeroImage(dto.images ?? []);
  return {
    id: project.uniqueId,
    title: project.displayName || 'Untitled Project',
    description: project.description || 'No description available',
    heroImage:
      coverImage === projectPlaceholder
        ? pickSceneScreenshot(dto.scenes ?? []) ?? projectPlaceholder
        : coverImage,
    duration: project.totalDuration ? Math.ceil(project.totalDuration / 60) : 0,
    tags: project.tags || [],
    status: computeProjectStatus((dto.scenes ?? []).map((s) => s.status)),
    locationInfo: parseLocationInfo(project.locationInfo),
    createdAt: updated,
    updatedAt: updated,
    storiesCount: (dto.stories ?? []).length,
    views: 0,
    completionRate: 0
  };
};
